const DAY_MS = 24 * 60 * 60 * 1000;
const RECENT_ACTIVITY_MS = 7 * DAY_MS;
const NEW_PROFILE_MS = 7 * DAY_MS;

const timestampMs = (value) => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (Number.isFinite(value?.seconds)) return value.seconds * 1000;
  return 0;
};

const availability = (expert) =>
  String(expert?.availability || expert?.expertProfile?.availability || 'offline')
    .toLowerCase()
    .trim();

const acceptedMs = (expert) => {
  const value = Number(expert?.lastCallAcceptedAtMs || 0) || timestampMs(expert?.lastCallAcceptedAt);
  // Answering a call is a lasting reliability signal. Keep every Maven who
  // has answered in the answered group; the timestamp still orders that group
  // so the people who answered most recently appear first.
  return value > 0 ? value : 0;
};

const activityMs = (expert) => {
  const value = Math.max(
    timestampMs(expert?.lastLogin),
    timestampMs(expert?.lastActiveAt),
    timestampMs(expert?.expertProfile?.lastLogin),
    timestampMs(expert?.expertProfile?.lastActiveAt),
  );
  return value > 0 && Date.now() - value <= RECENT_ACTIVITY_MS ? value : 0;
};

const createdMs = (expert) => Math.max(
  Number(expert?.createdAtMs || 0),
  timestampMs(expert?.createdAt),
  timestampMs(expert?.approvedAt),
  timestampMs(expert?.joinedAt),
  timestampMs(expert?.expertProfile?.createdAt),
);

const isNew = (expert) => {
  const value = createdMs(expert);
  return value > 0 && Date.now() - value <= NEW_PROFILE_MS;
};

const rank = (expert) => {
  if (expert?.applicationStatus && expert.applicationStatus !== 'approved') return 8;
  const online = availability(expert) === 'online';
  const newProfile = isNew(expert);
  if (online && acceptedMs(expert)) return 0;
  if (online && activityMs(expert) && !newProfile) return 1;
  if (online && newProfile) return 2;
  if (online) return 3;
  if (acceptedMs(expert)) return 4;
  if (activityMs(expert) && !newProfile) return 5;
  if (newProfile) return 6;
  return 7;
};

export const compareExpertDirectoryRank = (a, b) => {
  const aRank = rank(a);
  const bRank = rank(b);
  const rankDiff = aRank - bRank;
  if (rankDiff !== 0) return rankDiff;

  if (aRank === 0) {
    const acceptedDiff = acceptedMs(b) - acceptedMs(a);
    if (acceptedDiff !== 0) return acceptedDiff;
  }

  const signalDiff = Math.max(acceptedMs(b), activityMs(b), createdMs(b))
    - Math.max(acceptedMs(a), activityMs(a), createdMs(a));
  if (signalDiff !== 0) return signalDiff;

  const availabilityOrder = { online: 0, busy: 1, offline: 2 };
  const availabilityDiff = (availabilityOrder[availability(a)] ?? 2)
    - (availabilityOrder[availability(b)] ?? 2);
  if (availabilityDiff !== 0) return availabilityDiff;

  return Number(b?.averageRating || b?.rating || 0)
    - Number(a?.averageRating || a?.rating || 0);
};
